import React, { useEffect, useState } from 'react';
import { api } from '../state/api';
import { Card, Banner } from './ui';

/**
 * Runtime UX when an activated Plan Contract exists.
 * Outcome → Conditions → Constraint → Gate → Operating Outcome → Strategic Moves → Maintenance.
 */
export function PlanOsToday({ go }) {
  const [contract, setContract] = useState(null);
  const [derived, setDerived] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { contract: c } = await api.planOsGetContract();
        if (cancelled) return;
        setContract(c);
        if (c?.status === 'active') {
          const actions = [];
          const { derived: d } = await api.planOsDerive({
            asOf: new Date().toISOString(),
            evidenceFacts: [],
            actions,
          });
          if (!cancelled) setDerived(d);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return null;
  if (error) return <Banner tone="warn">{error}</Banner>;
  if (!contract || contract.status !== 'active' || !derived) return null;

  return (
    <div className="stack gap-md" data-testid="plan-os-today">
      <Card title="Primary Desired Outcome">
        <p className="lead">{derived.primaryDesiredOutcome?.statement || derived.primaryDesiredOutcome?.name}</p>
        <p className="muted">
          Health: <strong>{derived.outcomeHealth}</strong>
          {derived.outcomeHealthReasons?.length ? ` — ${derived.outcomeHealthReasons.join(' ')}` : ''}
        </p>
      </Card>

      <Card title="Outcome Conditions">
        <ul className="plain">
          {(derived.conditionStates || []).map((c) => (
            <li key={c.id}>
              <strong>{c.derivedStatus}</strong> {c.name}
              <span className="muted"> — {c.reason}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Current Primary Constraint">
        {derived.primaryConstraintUnknown || !derived.currentPrimaryConstraint ? (
          <p className="muted">UNKNOWN — insufficient evidence to select a dominant constraint.</p>
        ) : (
          <>
            <p>
              <strong>{derived.currentPrimaryConstraint.name}</strong>
            </p>
            <p className="muted">{derived.currentPrimaryConstraint.reason}</p>
          </>
        )}
      </Card>

      {derived.currentGate ? (
        <Card title="Current Gate">
          <p>
            <strong>{derived.currentGate.name}</strong> — {derived.currentGate.derivedStatus}
          </p>
          <p className="muted">{derived.currentGate.reason}</p>
        </Card>
      ) : null}

      {derived.operatingOutcome ? (
        <Card title="Operating Outcome">
          <p>{derived.operatingOutcome.statement}</p>
        </Card>
      ) : null}

      <Card title="Strategic Moves">
        {(derived.strategicMoves || []).length === 0 ? (
          <p className="muted">No ranked strategic moves yet.</p>
        ) : (
          <ol>
            {derived.strategicMoves.slice(0, 5).map((m) => (
              <li key={m.actionId}>
                <strong>{m.priorityClass}</strong> {m.actionId}
                <div className="muted">{m.reasonSelected}</div>
              </li>
            ))}
          </ol>
        )}
      </Card>

      <Card title="Required Maintenance">
        {(derived.requiredMaintenance || []).length === 0 ? (
          <p className="muted">None required right now.</p>
        ) : (
          <ul className="plain">
            {derived.requiredMaintenance.map((m) => (
              <li key={m.actionId}>
                {m.actionId} <span className="muted">{m.reasonSelected}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {(derived.exceptions || []).length ? (
        <Card title="Exceptions">
          <ul className="plain">
            {derived.exceptions.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </Card>
      ) : null}

      {typeof go === 'function' ? (
        <button type="button" className="btn sm" onClick={() => go('plan-configurator')}>
          Open Plan Configurator
        </button>
      ) : null}
    </div>
  );
}
